import { useEffect, useState } from "react"
import { isArrayEmpty } from "../utils/array"
import { useDebouncedValue, useOutsideClick } from "./hooks"
import { classes } from "./react"

export interface Props<T> {

    query(filter: string): PromiseLike<T[]>

    selectedItem: T
    textSelector(item: T): string
    setSelectedItem(item: T): void

    placeholder: string

    timeout?: number

    maxLength?: number

    disabled?: boolean

}

export function Typeahead<T>(props: Props<T>) {

    const { query, textSelector, selectedItem, setSelectedItem, placeholder, timeout, maxLength } = props

    const [state, setState] = useState({
        text: "",
        // This is true when the text was set by the user (i.e., typed), false otherwise (default text for the selected item)
        userText: false,
        open: false,
        selectedIndex: 0,
        results: undefined as T[]
    })

    const { text, open, selectedIndex, results } = state

    const setOpen = (open: boolean) => setState(s => ({ ...s, open }))

    const setText = (text: string, userText: boolean) => setState(s => ({ ...s, text, userText }))

    const items = results

    const rootRef = useOutsideClick(open, () => setOpen(false))

    const selectItem = (item: T) => {

        if (item) {

            setSelectedItem(item)

            setState(s => ({
                ...s,
                text: textSelector(item),
                open: false,
            }))

        }

    }

    const reset = () => setState(s => ({
        ...s,
        text: "",
        selectedIndex: 0,
        open: false
    }))

    useEffect(() => {

        if (selectedItem) {
            setText(textSelector(selectedItem), false)
        }
        else {
            reset()
        }

    }, [selectedItem])

    useKeyboardHandler(open, {
        selectedIndex,
        setSelectedIndex: selectedIndex => setState(s => ({ ...s, selectedIndex })),
        items,
        close: () => setOpen(false),
        selectCurrent: () => {
            const item = items ? items[selectedIndex] : undefined
            selectItem(item)
        }
    })

    const updateText = (text: string) => {

        setText(text, true)

        if (!text) {
            // This will not trigger a new query, and should probably
            // be interpreted as a reset
            setSelectedItem(undefined)
        }

    }

    const enableQuery = !(selectedItem && (textSelector(selectedItem) === text))

    useQuery({

        enable: enableQuery,

        filter: text,

        query,

        timeout: timeout ?? 300,

        setResults: r => {

            const openNext = state.userText && !isArrayEmpty(r)

            const toggleOpen = open !== openNext



            setState(s => ({
                ...s,
                open: toggleOpen ? openNext : s.open,
                selectedIndex: toggleOpen ? 0 : s.selectedIndex,
                results: r
            }))


        }

    }, [open])

    return <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>

        <input type="text" maxLength={maxLength} className="form-control" value={text} onChange={e => updateText(e.target.value)} placeholder={placeholder} disabled={props.disabled} />

        <ul style={{ margin: 0, display: (open ? "block" : "none") }} className="dropdown-menu">
            {
                items && items.map((k, index) => <ListItem key={index} item={k} index={index} />)
            }
        </ul>

    </div>


    function ListItem({ index, item }: { item: T, index: number }) {

        const text = textSelector(item)
        const selected = index === selectedIndex

        return <li className={classes(selected && "active")}>
            <a onClick={() => selectItem(item)}>
                <span>{text}</span>
            </a>
        </li>
    }

}

interface UseQueryProps<T> {
    enable: boolean
    filter: string
    query: (filter: string) => PromiseLike<T[]>
    setResults: (results: T[]) => void
    timeout?: number
}


function useQuery<T>({ enable, filter, query, setResults, timeout }: UseQueryProps<T>, deps?: React.DependencyList) {

    filter = useDebouncedValue(filter, timeout)

    useEffect(() => {

        if (!enable) {
            return
        }

        if (!filter) {
            setResults(undefined)
        }

        query(filter).then(setResults)

    }, [filter, query, ...deps])

}


interface KeyboardHandler<T> {

    items: T[]

    selectedIndex: number
    setSelectedIndex(index: number): void

    selectCurrent(): void

    close(): void

}

function useKeyboardHandler<T>(open: boolean, handler: KeyboardHandler<T>) {

    const { items, selectedIndex, setSelectedIndex, close, selectCurrent } = handler

    useEffect(() => {

        if (open && items) {

            const handleKeyDown = (e: KeyboardEvent) => {

                if (e.code == "Escape") {
                    close()
                    return
                }

                if ((e.code === "Enter") || (e.code === "Tab")) {
                    selectCurrent()
                    return
                }

                let i = 0
                const length = items.length

                if (e.code == "ArrowUp") {
                    i = - 1
                }
                else if (e.code == "ArrowDown") {
                    i = 1
                }

                if (i) {

                    i += selectedIndex
                    i = (i < 0) ? length - 1 : ((i >= length) ? 0 : i)

                    if (i !== selectedIndex) {
                        setSelectedIndex(i)
                    }

                }

            }

            document.addEventListener("keydown", handleKeyDown)

            return () => document.removeEventListener("keydown", handleKeyDown)

        }


    }, [open, items, selectedIndex])

}
